import React from "react";
import { Helmet } from "react-helmet-async";
import {
  absoluteImage,
  absoluteUrl,
  DEFAULT_ROBOTS,
  DEFAULT_SEO_DESCRIPTION,
  DEFAULT_SEO_KEYWORDS,
  DEFAULT_SEO_TITLE,
} from "../config/seo";

export type SeoProps = {
  title?: string;
  description?: string;
  keywords?: string;
  path?: string;
  image?: string;
  ogType?: string;
  ogImageType?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  siteName?: string;
  locale?: string;
  localeAlternate?: string;
  articlePublisher?: string;
  robots?: string;
  author?: string;
  publisher?: string;
  themeColor?: string;
  htmlLang?: string;
  twitterSite?: string;
  twitterCreator?: string;
  alternates?: Array<{ hrefLang: string; href: string }>;
  iconHref?: string;
  appleTouchIconHref?: string;
  jsonLd?: any;
};

const Seo = ({
  title,
  description,
  keywords,
  path,
  image,
  ogType,
  ogImageType,
  ogImageWidth,
  ogImageHeight,
  siteName,
  locale,
  localeAlternate,
  articlePublisher,
  robots,
  author,
  publisher,
  themeColor,
  htmlLang,
  twitterSite,
  twitterCreator,
  alternates,
  iconHref,
  appleTouchIconHref,
  jsonLd,
}: SeoProps) => {
  const canonicalUrl = path ? absoluteUrl(path) : "";
  const ogImage = image ? absoluteImage(image) : "";
  const resolvedIconHref = iconHref ? absoluteUrl(iconHref) : "";
  const resolvedAppleTouchIconHref = appleTouchIconHref
    ? absoluteUrl(appleTouchIconHref)
    : "";

  return (
    <Helmet htmlAttributes={htmlLang ? { lang: htmlLang } : undefined}>
      <title>{title || DEFAULT_SEO_TITLE}</title>
      <meta name="description" content={description || DEFAULT_SEO_DESCRIPTION} />
      <meta name="keywords" content={keywords || DEFAULT_SEO_KEYWORDS} />

      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

      {author ? <meta name="author" content={author} /> : null}
      {publisher ? <meta name="publisher" content={publisher} /> : null}
      {themeColor ? <meta name="theme-color" content={themeColor} /> : null}

      {resolvedIconHref ? <link rel="icon" href={resolvedIconHref} /> : null}
      {resolvedAppleTouchIconHref ? (
        <link rel="apple-touch-icon" href={resolvedAppleTouchIconHref} />
      ) : null}

      {alternates?.length
        ? alternates.map((alt) => (
          <link
            key={`${alt.hrefLang}:${alt.href}`}
            rel="alternate"
            hrefLang={alt.hrefLang}
            href={alt.href}
          />
        ))
        : null}

      {/* Open Graph */}
      <meta property="og:title" content={title || DEFAULT_SEO_TITLE} />
      <meta property="og:description" content={description || DEFAULT_SEO_DESCRIPTION} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {ogImage ? <meta property="og:image:secure_url" content={ogImage} /> : null}
      {ogImageType ? <meta property="og:image:type" content={ogImageType} /> : null}
      {typeof ogImageWidth === "number" ? (
        <meta property="og:image:width" content={String(ogImageWidth)} />
      ) : null}
      {typeof ogImageHeight === "number" ? (
        <meta property="og:image:height" content={String(ogImageHeight)} />
      ) : null}
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      <meta property="og:type" content={ogType || "website"} />
      {siteName ? <meta property="og:site_name" content={siteName} /> : null}
      {locale ? <meta property="og:locale" content={locale} /> : null}
      {localeAlternate ? (
        <meta property="og:locale:alternate" content={localeAlternate} />
      ) : null}
      {articlePublisher ? (
        <meta property="article:publisher" content={articlePublisher} />
      ) : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || DEFAULT_SEO_TITLE} />
      <meta name="twitter:description" content={description || DEFAULT_SEO_DESCRIPTION} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      {twitterSite ? <meta name="twitter:site" content={twitterSite} /> : null}
      {twitterCreator ? (
        <meta name="twitter:creator" content={twitterCreator} />
      ) : null}

      {/* Technical */}
      <meta name="robots" content={robots || DEFAULT_ROBOTS} />

      {jsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default Seo;

